/**
 * BootScene — first-frame setup before any asset loads.
 *
 * Responsibilities (build-plan-v2 §2.1):
 *   - Seed the global game registry with environment / app metadata so
 *     downstream scenes can read consistent boot-time values.
 *   - Generate the `__MISSING` placeholder texture so any later scene that
 *     fails an asset load has a visible fallback rather than an invisible
 *     `null` texture.
 *   - Hand off to `PreloaderScene` once setup is done.
 *
 * No network calls. No audio. No user-facing text. This scene is effectively
 * invisible — it runs for one frame and starts Preloader.
 */

import * as Phaser from 'phaser';
import {
  BOOT_SCENE_KEY,
  BUILD_TOOLS_SCENE_KEY,
  EDITOR_SCENE_KEY,
  PRELOADER_SCENE_KEY,
  MISSING_TEXTURE_KEY,
  WORLD_GALLERY_SCENE_KEY,
} from './sceneKeys';
import { TRANSITION_FRAMEWORK_TEST_SCENE_KEY } from './painted/TransitionFrameworkTestScene';
import { VIDEO_TRANSITION_SCENE_KEY } from './painted/VideoTransitionScene';
import { SHOWCASE_SCENE_KEY } from './ShowcaseScene';

/** Side length of the generated `__MISSING` placeholder texture, in px. */
const MISSING_TEXTURE_SIZE = 16;

/** App version string. Read from Vite-injected env when available; falls
 *  back to a literal so non-Vite test harnesses still see a sane value. */
function readAppVersion(): string {
  const env = (import.meta as unknown as { env?: Record<string, string> }).env;
  return env?.VITE_APP_VERSION ?? 'dev';
}

function readUrlParam(key: string): string | null {
  if (typeof window === 'undefined' || !window.location) return null;
  try {
    const fromSearch = new URLSearchParams(window.location.search).get(key);
    if (fromSearch !== null) return fromSearch;
    // Fallback: handle /key=value in the pathname (common mis-typed URLs)
    const match = window.location.pathname.match(
      new RegExp(`(?:^|/)${key}=([^/&]*)`)
    );
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

function readDevSceneFlag(): string | null {
  return readUrlParam('scene');
}

function readEditRoomFlag(): string | null {
  return readUrlParam('edit');
}

function isDevMode(): boolean {
  const env = (import.meta as unknown as { env?: Record<string, string | boolean> }).env;
  return !!env?.DEV;
}

/**
 * Build tools are now opt-in via `?mode=build` rather than the DEV default.
 *
 * The Small Hours is one scene, and a dev session should open *in* it — the
 * scene browser and its debug overlays were the first thing anyone saw, which
 * is the wrong default when the whole product is a single grove.
 */
function shouldBootBuildTools(): boolean {
  const mode = readUrlParam('mode');
  if (mode === 'build') return true;
  return false;
}

export class BootScene extends Phaser.Scene {
  public static readonly KEY = BOOT_SCENE_KEY;

  constructor() {
    super({ key: BOOT_SCENE_KEY });
  }

  create(): void {
    this.seedRegistry();
    this.installMissingTexture();

    if (readEditRoomFlag() !== null) {
      this.scene.start(EDITOR_SCENE_KEY);
      return;
    }

    if (shouldBootBuildTools()) {
      this.scene.start(BUILD_TOOLS_SCENE_KEY);
      return;
    }

    const devScene = readDevSceneFlag();
    if (devScene === 'worldGallery') {
      this.scene.start(WORLD_GALLERY_SCENE_KEY);
      return;
    }
    if (devScene === 'transitionTest') {
      this.scene.start(TRANSITION_FRAMEWORK_TEST_SCENE_KEY);
      return;
    }
    if (devScene === 'showcase') {
      this.scene.start(SHOWCASE_SCENE_KEY);
      return;
    }
    if (devScene === 'videoTransition') {
      this.scene.start(VIDEO_TRANSITION_SCENE_KEY, {
        videoUrl: 'assets/generated/transition-videos/scene-3-forest-hollow_parallax-walk_20260505-003917.webm',
        sourceSceneKey: VIDEO_TRANSITION_SCENE_KEY,
        targetSceneKey: 'ForestHollowScene',
      });
      return;
    }

    this.scene.start(PRELOADER_SCENE_KEY);
  }

  /**
   * Write app-wide registry keys read by later scenes. Keys mirror
   * `BOOT_REGISTRY_KEYS` in `sceneKeys.ts` — keep them in sync.
   */
  private seedRegistry(): void {
    this.registry.set('app:version', readAppVersion());
    this.registry.set('app:theme', 'softening');
    this.registry.set('app:bootedAtMs', Date.now());
    // Mute toggle persisted by TitleScene — initialise to false so audio
    // managers reading the flag before TitleScene runs see a defined value.
    if (this.registry.get('app:audioMuted') === undefined) {
      this.registry.set('app:audioMuted', false);
    }
  }

  /**
   * Build a 16×16 magenta-on-black checker placeholder via a Graphics object
   * and stamp it into the texture cache under `MISSING_TEXTURE_KEY`. Idempotent:
   * if the texture already exists (HMR / scene restart), we leave it alone.
   */
  private installMissingTexture(): void {
    if (this.textures.exists(MISSING_TEXTURE_KEY)) return;
    const half = MISSING_TEXTURE_SIZE / 2;
    const g = this.make.graphics({ x: 0, y: 0 }, false);
    g.fillStyle(0x000000, 1);
    g.fillRect(0, 0, MISSING_TEXTURE_SIZE, MISSING_TEXTURE_SIZE);
    g.fillStyle(0xff00ff, 1);
    g.fillRect(0, 0, half, half);
    g.fillRect(half, half, half, half);
    g.generateTexture(MISSING_TEXTURE_KEY, MISSING_TEXTURE_SIZE, MISSING_TEXTURE_SIZE);
    g.destroy();
  }
}
